'use client'

import { Terminal } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { Button } from '~/components/component-library'
import { DepositButton } from '~/components/forms/deposit/DepositButton'
import { RequestButton } from '~/components/forms/invoice/RequestButton'
import { SwapButton } from '~/components/forms/swap/SwapButton'
import { SendButton } from '~/components/forms/transfer/SendButton'
import { useActiveOrgMember } from '~/hooks/org'
import { IAddress } from '~/types'
import { cn } from '~/utils/tailwind-utils'

export const AccountActionButtons = ({
  account,
  isDisabled,
  disabledReason,
  className,
}: {
  account?: IAddress
  isDisabled?: boolean
  disabledReason?: string
  className?: string
}) => {
  const orgMember = useActiveOrgMember()
  const canCreateInvoice = orgMember?.role === 'OWNER'

  return (
    // Scroll the row on narrow widths (like the legacy toolbars) instead of
    // squeezing the buttons or overflowing the page. md:pr-8 keeps the right
    // edge aligned with the transaction feed below.
    <div
      className={cn(
        'no-scrollbar flex w-full min-w-0 max-w-full items-center gap-2 overflow-x-auto md:pr-8',
        className,
      )}
    >
      <SendButton
        defaultSender={account}
        isDisabled={isDisabled}
        disabledReason={disabledReason}
      />
      <SwapButton
        defaultSender={account}
        isDisabled={isDisabled}
        disabledReason={disabledReason}
      />
      <DepositButton defaultAccount={account} />
      <RequestButton
        defaultAccount={account}
        isDisabled={!canCreateInvoice}
        disabledReason={
          canCreateInvoice ? undefined : 'Only owners can create new invoices'
        }
      />
      {account && (
        // Custom txn needs a specific account, so it's omitted from the
        // dashboard's account-less action row.
        <SplitsLink path={`/custom-txn?account=${account}`}>
          <Button
            variant="secondary"
            prefix={<Terminal />}
            isDisabled={isDisabled}
            disabledReason={disabledReason}
          >
            Custom
          </Button>
        </SplitsLink>
      )}
    </div>
  )
}
